import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import {
  GET_ADMIN_CONSOLE_OPS_ALLOW_BACKUP_SESSIONS,
  GET_ADMIN_CONSOLE_OPS_SESSION_LIMITS,
} from './admin-console-operations.gql';
import { GetAdminConsoleOpsSessionLimits } from './__generated__/GetAdminConsoleOpsSessionLimits';
import { GetAdminConsoleOpsAllowBackupSessions } from './__generated__/GetAdminConsoleOpsAllowBackupSessions';

const MIN_ALLOWED = 1;
const MAX_ALLOWED = 500;

const isValidCount = (value: number | '') =>
  value === '' || (typeof value === 'number' && value >= MIN_ALLOWED && value <= MAX_ALLOWED);

export function SessionLimits() {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetAdminConsoleOpsSessionLimits, undefined>(
    GET_ADMIN_CONSOLE_OPS_SESSION_LIMITS
  );

  const { data: backupData, refetch: backupRefetch } = useCampfireQuery<
    GetAdminConsoleOpsAllowBackupSessions,
    undefined
  >(GET_ADMIN_CONSOLE_OPS_ALLOW_BACKUP_SESSIONS);

  const [maxEnabled, setMaxEnabled] = useState(false);
  const [maxSessions, setMaxSessions] = useState<number | ''>('');
  const [minEnabled, setMinEnabled] = useState(false);
  const [minSessions, setMinSessions] = useState<number | ''>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [allowBackupSessions, setAllowBackupSessions] = useState(false);

  useEffect(() => {
    const serverMax = data?.tm.tenant?.config?.maxSessionsPerVolunteer;
    setMaxEnabled(serverMax !== undefined && serverMax !== null);
    setMaxSessions(serverMax ?? '');

    const serverMin = data?.tm.tenant?.config?.minSessionsPerApplicant;
    setMinEnabled(serverMin !== undefined && serverMin !== null);
    setMinSessions(serverMin ?? '');

    setHasChanges(false);
  }, [data]);

  useEffect(() => {
    setAllowBackupSessions(
      backupData?.vm.configItem?.configItemValue?.flagValue ?? backupData?.vm.configItem?.defaultValue ?? false
    );
  }, [backupData]);

  const onAllowBackupChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !allowBackupSessions,
          type: 'allow-backup-sessions',
        },
      })
      .then(() => {
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Settings updated',
        });
        backupRefetch();
      });
  };

  const parseCount = (raw: string): number | '' => {
    if (raw === '') return '';
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? '' : parsed;
  };

  const handleSave = () => {
    const maxValue = maxEnabled && maxSessions !== '' ? maxSessions : null;
    const minValue = minEnabled && minSessions !== '' ? minSessions : null;

    campfireMutation
      .run({
        url: `${apiUrl}/tm/config/max-sessions-per-volunteer`,
        method: 'post',
        data: { maxSessions: maxValue },
      })
      .then(() =>
        campfireMutation.run({
          url: `${apiUrl}/tm/config/min-sessions-per-applicant`,
          method: 'post',
          data: { minSessions: minValue },
        })
      )
      .then(() => {
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Session limits updated',
        });
        setHasChanges(false);
      })
      .catch(() => {
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Failed to update session limits',
        });
      });
  };

  const maxValid = isValidCount(maxSessions);
  const minValid = isValidCount(minSessions);
  const canSave =
    hasChanges &&
    (!maxEnabled || (maxSessions !== '' && maxValid)) &&
    (!minEnabled || (minSessions !== '' && minValid));

  return (
    <Box mt={2}>
      <Box display='flex' alignItems='center'>
        <Switch
          checked={maxEnabled}
          onChange={() => {
            setMaxEnabled(!maxEnabled);
            if (maxEnabled) {
              setMaxSessions('');
            }
            setHasChanges(true);
          }}
          color='primary'
          inputProps={{ 'aria-label': 'enable maximum sessions per volunteer' }}
        />
        <Box ml={1}>
          <Typography style={{ fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center' }}>
            Limit Upcoming Sessions
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
            Cap how many upcoming sessions a volunteer can be rostered on at once
          </Typography>
        </Box>
      </Box>

      {maxEnabled && (
        <Box mt={3} ml={7}>
          <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Maximum Sessions</Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '16px' }}>
            Volunteers will not be rostered onto more than this many upcoming sessions. They can still indicate
            availability for as many sessions as they like. Set between {MIN_ALLOWED} and {MAX_ALLOWED}.
          </Typography>
          <TextField
            type='number'
            value={maxSessions}
            onChange={(e) => {
              setMaxSessions(parseCount(e.target.value));
              setHasChanges(true);
            }}
            variant='outlined'
            label='Sessions'
            placeholder='e.g. 5'
            inputProps={{ min: MIN_ALLOWED, max: MAX_ALLOWED }}
            style={{ width: 150 }}
            error={maxSessions !== '' && !maxValid}
            helperText={maxSessions !== '' && !maxValid ? `Must be between ${MIN_ALLOWED} and ${MAX_ALLOWED}` : ''}
          />

          <Box display='flex' alignItems='center' mt={2} ml={-1.5}>
            <Switch
              checked={allowBackupSessions}
              onChange={onAllowBackupChange}
              color='primary'
              inputProps={{ 'aria-label': 'allow backup sessions' }}
            />
            <Box ml={1}>
              <Typography style={{ fontWeight: 500, fontSize: '14px' }}>Allow Backup Sessions</Typography>
              <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                Volunteers can select additional sessions beyond the limit. These are saved as availability only, so a
                manager can roster them if a spot opens up.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box display='flex' alignItems='center' mt={3}>
        <Switch
          checked={minEnabled}
          onChange={() => {
            setMinEnabled(!minEnabled);
            if (minEnabled) {
              setMinSessions('');
            }
            setHasChanges(true);
          }}
          color='primary'
          inputProps={{ 'aria-label': 'require minimum sessions' }}
        />
        <Box ml={1}>
          <Typography style={{ fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center' }}>
            Require Minimum Sessions
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
            Applicants must select a minimum number of event sessions during their application
          </Typography>
        </Box>
      </Box>

      {minEnabled && (
        <Box mt={3} ml={7}>
          <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Minimum Sessions</Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '16px' }}>
            Applicants cannot confirm their event session selection until they have chosen at least this many. This does
            not affect existing volunteers. Set between {MIN_ALLOWED} and {MAX_ALLOWED}.
          </Typography>
          <TextField
            type='number'
            value={minSessions}
            onChange={(e) => {
              setMinSessions(parseCount(e.target.value));
              setHasChanges(true);
            }}
            variant='outlined'
            label='Sessions'
            placeholder='e.g. 2'
            inputProps={{ min: MIN_ALLOWED, max: MAX_ALLOWED }}
            style={{ width: 150 }}
            error={minSessions !== '' && !minValid}
            helperText={minSessions !== '' && !minValid ? `Must be between ${MIN_ALLOWED} and ${MAX_ALLOWED}` : ''}
          />
        </Box>
      )}

      {hasChanges && (
        <Box display='flex' justifyContent='flex-end' mt={3}>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={!canSave}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '7px 12px',
              minWidth: '40px',
            }}
          >
            Save
          </TabletButton>
        </Box>
      )}
    </Box>
  );
}
